/**
 * Node Drag Hook
 * Handles node drag and drop from palette to canvas
 */

import { useCallback } from 'react';
import { useWorkflowStore } from '@flow-studio/store';
import { NodeType, WorkflowNode, ProcessElementPort } from '@flow-studio/types';
import { nodeEvents, generateId } from '@flow-studio/services';


// Maps category (from templates table) to a React Flow shape key.
// Must stay in sync with workflowMappers.categoryToShape and NodeFactoryService.getReactFlowNodeType.
// Exception: node code "ai-function" maps to the "ai-function-node" shape regardless of category
// (handled at the call sites, since that code spans several functional categories).
const CATEGORY_TO_SHAPE: Record<string, string> = {
  trigger:   'circle',
  logic:     'diamond',
  'ai-agent': 'ai-agent',
  workflow:  'subflow',
};
function categoryToShape(category: string): string {
  return CATEGORY_TO_SHAPE[category] ?? 'custom';
}

export const useNodeDrag = () => {
  const { addNode, nodeTypes } = useWorkflowStore();

  const onDragStart = useCallback(
    (event: React.DragEvent, nodeType: NodeType) => {
      const transferKey = String(nodeType.id);
      event.dataTransfer.setData('application/reactflow', transferKey);
      event.dataTransfer.effectAllowed = 'move';
    },
    []
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent, reactFlowInstance: { screenToFlowPosition: (pos: { x: number; y: number }) => { x: number; y: number } }) => {
      event.preventDefault();

      const transferKey = event.dataTransfer.getData('application/reactflow');
      if (!transferKey) {
        console.warn('[useNodeDrag] Drop had no application/reactflow payload — drag likely did not originate from the node palette.');
        return;
      }

      const nodeType = nodeTypes.find((nt) => String(nt.id) === transferKey);
      if (!nodeType) {
        console.warn(`[useNodeDrag] No NodeType found for id "${transferKey}" — palette nodeTypes may be stale or not yet loaded.`);
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNodeId = generateId('node');
      const isTrigger = nodeType.category === 'trigger';
      // AI Function shares one code across many functional categories — shape by code, not category.
      const rfNodeType = nodeType.code === 'ai-function' ? 'ai-function-node' : categoryToShape(nodeType.category);

      const ports: ProcessElementPort[] = [
        ...nodeType.defaultInputPorts.map((p, i) => ({ ...p, portIndex: i })),
        ...nodeType.defaultOutputPorts.map((p, i) => ({ ...p, portIndex: i })),
      ];

      // Seed processElement.configuration and connector.configuration from the node template's
      // settings.data block so they are persisted on first save without any user interaction.
      const templateData = (nodeType as any).settings?.data ?? {};
      const templatePeConfig = templateData.processElement?.configuration ?? {};
      const templateConnectorConfig = templateData.connector?.configuration ?? {};

      // Merge template processElement config; stamp live nodeType visual fields on top
      // only when they are defined — avoids overwriting richer values already in the template's ui block.
      const configuration = {
        ...templatePeConfig,
        designer: {
          ...(templatePeConfig.designer ?? {}),
          ui: {
            ...(templatePeConfig.designer?.ui ?? {}),
            color: nodeType.color,
            ...(nodeType.iconUrl ? { iconUrl: nodeType.iconUrl } : {}),
            ...(nodeType.icon    ? { icon: nodeType.icon }       : {}),
            ...(nodeType.nodeStyle ? { nodeStyle: nodeType.nodeStyle } : {}),
            category: nodeType.category,
            nodeType: nodeType.code,
            ...(nodeType.id ? { dataTemplateID: nodeType.id } : {}),
          },
        },
      };

      const newNode: WorkflowNode = {
        id: newNodeId,
        type: rfNodeType,
        position,
        state: 'new',
        templateReference: nodeType,
        data: {
          processElement: {
            processElementKey: newNodeId,
            name: nodeType.code,
            displayName: nodeType.displayName,
            description: nodeType.description,
            isTrigger,
            isDisabled: false,
            continueOnFail: false,
            alwaysOutputData: false,
            retryOnFail: false,
            maxRetries: 0,
            configuration,
            ports,
            connector: { configuration: { ...templateConnectorConfig } },
          },
          runtime: {
            executionStatus: 'idle',
            executionMode: isTrigger ? 'trigger' : 'always',
            collapsed: false,
          },
        },
      };

      addNode(newNode);

      // Emit NodeAdded event
      nodeEvents.added(newNode, {
        source: 'drag-drop',
        templateCode: nodeType.code,
      });
    },
    [nodeTypes, addNode]
  );

  return {
    onDragStart,
    onDragOver,
    onDrop,
  };
};
